import { useEffect, useState, useCallback, useRef } from 'react';
import { adminHeaders } from '../../lib/adminAuth';
import './PassportPage.css';
import OtaReservationForm from '../../components/admin/OtaReservationForm';
import IssuedUrlPanel from '../../components/admin/IssuedUrlPanel';
import type { OtaFormState, IssuedInfo } from '../../components/admin/otaTypes';

import { tx } from '../../lib/adminI18n';
/**
 * 管理画面「パスポート管理」
 *   - 予約別の登録状況一覧（自社予約 + OTA予約）
 *   - 詳細（ゲスト情報・ファイル閲覧/DL/削除）
 *   - アップロードURL再送信（自社=メール / OTA=URL表示してコピー）
 *   - OTA予約の手動登録（Airbnb/Booking.com等）→ URLを発行してコピー
 * すべて /api/admin/passport/*（admin-login 認証）。閲覧/DLはサーバー側で監査ログ記録。
 */

interface ReservationRow {
  id: string;
  property_name: string;
  checkin_date: string;
  checkout_date: string;
  guest_count: number;
  registered_count: number;
  status: string;
  source_channel: string;
  ota_reservation_code: string | null;
  expired: boolean;
  cancelled: boolean;
}
interface FileRow { id: string; content_type: string; size_bytes: number; upload_seq: number; created_at: string; }
interface GuestRow {
  id: string; seq_no: number; name: string; birthdate: string; gender: string;
  nationality: string; passport_number: string;
  address: string; prev_accommodation: string; next_destination: string;
  status: string; files: FileRow[];
}
interface Detail {
  reservation_id: string; property_name: string; checkin_date: string;
  checkout_date: string; guest_count: number; status: string; guests: GuestRow[];
}
interface PropertyOpt { id: string; name: string; }

const GENDER_LABEL: Record<string, string> = { get male() { return tx('pp.0'); }, get female() { return tx('pp.1'); }, get other() { return tx('pp.2'); } };
const CHANNEL_LABEL: Record<string, string> = { get direct() { return tx('pp.3'); }, airbnb: 'Airbnb', booking: 'Booking.com', get other_ota() { return tx('pp.4'); } };

// 画像ファイル（サムネイル表示対象）かどうか。PDFはサムネイル生成しない。
function isImageType(ct: string): boolean {
  return ct === 'image/jpeg' || ct === 'image/png';
}

export default function AdminPassportPage() {
  const [rows, setRows] = useState<ReservationRow[]>([]);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [copiedUrl, setCopiedUrl] = useState('');

  // 一覧の表示制御: チェックイン昇順/降順・物件名フィルター・過去データ表示切替。
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [showPast, setShowPast] = useState(false); // デフォルトは過去データを非表示

  // 画像サムネイル用のオブジェクトURL（fileId → blob URL）とライトボックス表示状態。
  // 認証付きエンドポイントから取得するため、blobを取ってobjectURLを生成する。
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [lightbox, setLightbox] = useState<string | null>(null);
  const objectUrlsRef = useRef<string[]>([]);

  // 生成したobjectURLをまとめて破棄する。
  const revokeAllObjectUrls = useCallback(() => {
    for (const u of objectUrlsRef.current) URL.revokeObjectURL(u);
    objectUrlsRef.current = [];
  }, []);

  // アンマウント時にobjectURLを解放。
  useEffect(() => () => revokeAllObjectUrls(), [revokeAllObjectUrls]);

  // OTA登録フォーム
  const [properties, setProperties] = useState<PropertyOpt[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [otaForm, setOtaForm] = useState<OtaFormState>({
    property_id: '', checkin_date: '', checkout_date: '', guest_count: 1,
    guest_name: '', source_channel: 'airbnb', ota_reservation_code: '',
  });
  const [issued, setIssued] = useState<IssuedInfo | null>(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch('/api/admin/passport/reservations', { headers: adminHeaders() });
    const d = await res.json().catch(() => ({}));
    setRows(d.reservations || []);
    setLoading(false);
  }, []);

  const loadProps = useCallback(async () => {
    const res = await fetch('/api/admin/properties', { headers: adminHeaders() });
    const d = await res.json().catch(() => ({}));
    const list = (d.properties || d || []) as any[];
    setProperties(list.map((p) => ({ id: p.id, name: p.name })));
  }, []);

  useEffect(() => { load(); loadProps(); }, [load, loadProps]);

  async function openDetail(id: string) {
    setMsg('');
    const res = await fetch(`/api/admin/passport/reservations/${id}`, { headers: adminHeaders() });
    const d = await res.json().catch(() => ({}));
    if (res.ok) {
      setDetail(d);
      loadThumbs(d);
    }
  }

  // 詳細内の画像ファイルを取得してサムネイル用のobjectURLを生成する。
  async function loadThumbs(d: Detail) {
    // 以前のサムネイルは破棄してリセット
    revokeAllObjectUrls();
    setThumbs({});
    const imageFiles: FileRow[] = [];
    for (const g of d.guests || []) {
      for (const f of g.files || []) {
        if (isImageType(f.content_type)) imageFiles.push(f);
      }
    }
    for (const f of imageFiles) {
      try {
        const res = await fetch(`/api/admin/passport/files/${f.id}`, { headers: adminHeaders() });
        if (!res.ok) continue;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        objectUrlsRef.current.push(url);
        setThumbs((prev) => ({ ...prev, [f.id]: url }));
      } catch { /* 取得失敗したファイルはスキップ */ }
    }
  }

  async function copyUrl(url: string) {
    try { await navigator.clipboard.writeText(url); setCopiedUrl(url); setTimeout(() => setCopiedUrl(''), 2000); }
    catch { /* クリップボード不可環境では手動選択 */ }
  }

  async function openFile(fileId: string, download: boolean) {
    const res = await fetch(`/api/admin/passport/files/${fileId}${download ? '?download=1' : ''}`, { headers: adminHeaders() });
    if (!res.ok) { setMsg(tx('pp.5')); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    if (download) {
      const a = document.createElement('a');
      a.href = url; a.download = fileId; document.body.appendChild(a); a.click(); a.remove();
    } else { window.open(url, '_blank', 'noopener'); }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  // 宿泊者名簿CSVをダウンロード（認証ヘッダ付きでfetch→blob保存）
  async function downloadRegistry(id: string) {
    const res = await fetch(`/api/admin/passport/reservations/${id}/registry.csv`, { headers: adminHeaders() });
    if (!res.ok) { setMsg(tx('pp.6')); return; }
    const blob = await res.blob();
    // Content-Dispositionからファイル名を拾う（なければ既定名）
    let fname = `registry_${id}.csv`;
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="([^"]+)"/);
    if (m) fname = m[1];
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  async function deleteFile(fileId: string) {
    if (!confirm(tx('pp.7'))) return;
    const res = await fetch(`/api/admin/passport/files/${fileId}`, { method: 'DELETE', headers: adminHeaders() });
    if (res.ok && detail) await openDetail(detail.reservation_id);
  }

  // ゲスト情報リセット: 登録済みゲストの入力情報と画像を削除し、未登録の空枠に戻す（枠は残す）。
  async function resetGuest(guestId: string) {
    if (!confirm(tx('pp.8'))) return;
    const res = await fetch(`/api/admin/passport/guests/${guestId}`, { method: 'DELETE', headers: adminHeaders() });
    if (res.ok && detail) { setMsg(tx('pp.9')); await openDetail(detail.reservation_id); }
    else if (!res.ok) { setMsg(tx('pp.10')); }
  }

  // URL発行（送信はしない）。自社予約はメール文面プレビュー、OTAは固定URL+予約番号を表示。
  async function issue(id: string) {
    setMsg(''); setIssued(null); setSending(false);
    const res = await fetch('/api/admin/passport/issue', {
      method: 'POST',
      headers: adminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ reservation_id: id }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(tx('pp.11')); return; }
    if (d.is_ota) {
      setIssued({ reservationId: id, isOta: true, url: d.url, codeUrl: d.code_url || undefined, code: d.access_code || undefined, checkin: d.checkin_date });
      setMsg(tx('pp.12'));
    } else {
      setIssued({
        reservationId: id, isOta: false, url: d.url,
        mailTo: d.mail_to, mailSubject: d.mail_subject, mailText: d.mail_text,
      });
      setMsg(tx('pp.13'));
    }
  }

  // 即時救済: その場で有効なURLを1本作り直して表示（メールは送らない）。
  // URL無効化などでゲストが入れないときに、ホストが直接URLを渡すための操作。
  async function rescue(id: string) {
    // 旧URLは即時に無効化されるため、実行前に確認を取る。
    if (!confirm(
      tx('pp.14') +
      tx('pp.15') +
      tx('pp.16') +
      tx('pp.17')
    )) return;
    setMsg(''); setIssued(null); setSending(false);
    const res = await fetch('/api/admin/passport/rescue', {
      method: 'POST',
      headers: adminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ reservation_id: id }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(tx('pp.18')); return; }
    if (d.is_ota) {
      setIssued({ reservationId: id, isOta: true, rescue: true, url: d.url, codeUrl: d.code_url || undefined, code: d.access_code || undefined, checkin: d.checkin_date });
      setMsg(tx('pp.19'));
    } else {
      setIssued({ reservationId: id, isOta: false, rescue: true, url: d.url, checkin: d.checkin_date });
      setMsg(tx('pp.20'));
    }
  }

  // 発行済みの内容でメールを送信（自社予約のみ）
  async function sendMail(id: string) {
    setSending(true);
    try {
      const res = await fetch('/api/admin/passport/send-mail', {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ reservation_id: id, subject: issued?.mailSubject, text: issued?.mailText }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg(tx('pp.21')); return; }
      setMsg(d.skipped ? tx('pp.22') : tx('pp.23'));
      setIssued(null);
    } finally { setSending(false); }
  }

  async function submitOta(e: React.FormEvent) {
    e.preventDefault();
    setMsg(''); setIssued(null);
    const res = await fetch('/api/admin/passport/ota-reservations', {
      method: 'POST',
      headers: adminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(otaForm),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(d.error || tx('pp.24')); return; }
    setIssued({ reservationId: d.reservation_id, isOta: true, url: d.fixed_url, codeUrl: d.code_url || undefined, code: d.access_code, checkin: d.checkin_date });
    setMsg(tx('pp.25'));
    setShowForm(false);
    setOtaForm({ property_id: '', checkin_date: '', checkout_date: '', guest_count: 1, guest_name: '', source_channel: 'airbnb', ota_reservation_code: '' });
    await load();
  }

  // 物件名の選択肢（重複を除いた一覧）。フィルターのプルダウンに使う。
  const propertyNames = Array.from(new Set(rows.map((r) => r.property_name))).sort((a, b) => a.localeCompare(b, 'ja'));

  // 今日（JST）の YYYY-MM-DD。チェックイン日が過去かどうかの判定に使う。
  const todayJst = new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);

  // フィルター（物件名・過去データ）→ ソート（チェックイン昇順/降順）の順に適用した表示用リスト。
  const visibleRows = rows
    .filter((r) => !propertyFilter || r.property_name === propertyFilter)
    .filter((r) => showPast || r.checkin_date >= todayJst)
    .slice()
    .sort((a, b) => {
      const cmp = a.checkin_date.localeCompare(b.checkin_date);
      return sortDir === 'asc' ? cmp : -cmp;
    });

  function toggleSort() {
    setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
  }

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="admin-page-title">{tx('pp.26')}</h1>
        <button className="pp-btn" onClick={() => { setShowForm((v) => !v); setIssued(null); }}>
          {showForm ? tx('pp.27') : tx('pp.28')}
        </button>
      </div>

      {msg && <p style={{ background: '#eff6ff', border: '1px solid #bfdbfe', padding: '.6rem .8rem', borderRadius: 8 }}>{msg}</p>}

      {issued && (
        <div className="pp-detail-overlay" onClick={() => { if (!sending) setIssued(null); }}>
        <div className="pp-detail pp-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.6rem' }}>
            <strong>{tx('pp.29')}</strong>
            <button type="button" className="pp-btn-ghost" onClick={() => setIssued(null)} disabled={sending} aria-label={tx('pp.27')}>×</button>
          </div>
        <IssuedUrlPanel
          issued={issued}
          copiedUrl={copiedUrl}
          onCopy={copyUrl}
          onClose={() => setIssued(null)}
          onEditSubject={(v) => setIssued((prev) => prev ? { ...prev, mailSubject: v } : prev)}
          onEditText={(v) => setIssued((prev) => prev ? { ...prev, mailText: v } : prev)}
          onSendMail={sendMail}
          sending={sending}
        />
        </div>
        </div>
      )}

      {showForm && (
        <OtaReservationForm form={otaForm} properties={properties} onChange={setOtaForm} onSubmit={submitOta} />
      )}

      {/* フィルターバー: 物件名フィルター / 過去データ表示切替 */}
      {!loading && (
        <div className="pp-filter-bar">
          <label className="pp-filter-item">
            <span className="pp-filter-label">{tx('pp.30')}</span>
            <select className="pp-input" value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)}>
              <option value="">{tx('pp.31')}</option>
              {propertyNames.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
          <label className="pp-filter-check">
            <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} />
            <span>{tx('pp.32')}</span>
          </label>
          <span className="pp-filter-count">{visibleRows.length}{tx('pp.33')}</span>
        </div>
      )}

      {loading ? <p>{tx('pp.34')}</p> : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{tx('pp.35')}</th>
              <th>{tx('pp.36')}</th>
              <th className="pp-sortable" onClick={toggleSort} title={tx('pp.37')}>
                {tx('pp.38')} {sortDir === 'asc' ? '▲' : '▼'}
              </th>
              <th>{tx('pp.39')}</th>
              <th>{tx('pp.40')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9ca3af', padding: '1.5rem' }}>{tx('pp.41')}</td></tr>
            )}
            {visibleRows.map((r) => (
              <tr key={r.id}>
                <td data-label={tx('pp.35')}>{r.property_name}<br /><span style={{ color: '#6b7280', fontSize: '.85rem' }}>{r.ota_reservation_code || r.id}</span></td>
                <td data-label={tx('pp.36')}>
                  <span className={`pp-badge ${r.source_channel === 'direct' ? 'direct' : 'ota'}`}>
                    {CHANNEL_LABEL[r.source_channel] || r.source_channel}
                  </span>
                </td>
                <td data-label={tx('pp.38')}>{r.checkin_date}</td>
                <td data-label={tx('pp.39')}>
                  {r.registered_count >= r.guest_count
                    ? <span className="pp-done">{tx('pp.42')} {r.registered_count}/{r.guest_count}</span>
                    : <span className="pp-pending">{tx('pp.43')} {r.registered_count}/{r.guest_count}</span>}
                </td>
                <td data-label={tx('pp.40')}>{r.cancelled ? tx('pp.44') : r.expired ? tx('pp.45') : tx('pp.46')}</td>
                <td data-label={tx('pp.47')} className="col-actions" style={{ whiteSpace: 'nowrap' }}>
                  <button className="pp-btn-sm" onClick={() => openDetail(r.id)}>{tx('pp.48')}</button>{' '}
                  {!r.cancelled && <button className="pp-btn-sm" onClick={() => issue(r.id)}>{tx('pp.49')}</button>}{' '}
                  {!r.cancelled && <button className="pp-btn-ghost" onClick={() => rescue(r.id)} title={tx('pp.50')}>{tx('pp.51')}</button>}
                  {' '}<button className="pp-btn-ghost" onClick={() => downloadRegistry(r.id)} title={tx('pp.52')}>{tx('pp.53')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detail && (
        <div className="pp-detail-overlay" onClick={() => setDetail(null)}>
        <div className="pp-detail pp-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700 }}>{detail.property_name}{tx('pp.54')}{detail.reservation_id}{tx('pp.55')}</h2>
            <div style={{ display: 'flex', gap: '.5rem' }}>
              <button className="pp-btn-sm" onClick={() => downloadRegistry(detail.reservation_id)} title={tx('pp.52')}>{tx('pp.56')}</button>
              <button className="pp-btn-ghost" onClick={() => setDetail(null)}>{tx('pp.27')}</button>
            </div>
          </div>
          <p style={{ color: '#6b7280', fontSize: '.9rem' }}>
            {detail.checkin_date} 〜 {detail.checkout_date} / {detail.guest_count}名 / {detail.status}
          </p>
          {detail.guests.map((g) => (
            <div key={g.id} style={{ borderTop: '1px solid #f3f4f6', padding: '.9rem 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.5rem' }}>
                <div>
                  <strong>{tx('pp.58')}{g.seq_no}{g.name ? tx('pp.59', { p0: g.name }) : tx('pp.60')}</strong>
                  {g.status === 'registered' && <span style={{ color: '#16a34a', marginLeft: 8, fontSize: '.85rem' }}>{tx('pp.61')}</span>}
                </div>
                {/* 登録済み（または情報入力済み）のゲストのみ削除ボタンを表示。未登録の空枠には出さない。 */}
                {(g.status === 'registered' || g.name) && (
                  <button className="pp-btn-ghost" style={{ padding: '.25rem .6rem', color: '#dc2626', borderColor: '#fca5a5' }}
                    onClick={() => resetGuest(g.id)} title={tx('pp.62')}>
                    {tx('pp.63')}
                  </button>
                )}
              </div>
              {g.name && (
                <div style={{ color: '#374151', fontSize: '.9rem', margin: '.4rem 0' }}>
                  {tx('pp.64')} {g.birthdate || '—'} {tx('adm.gender')} {GENDER_LABEL[g.gender] || '—'} {tx('pp.65')} {g.nationality || '—'} {tx('adm.passportNo')} {g.passport_number || '—'}
                  <br />{tx('pp.66')} {g.address || '—'} {tx('adm.prevAcc')} {g.prev_accommodation || '—'} {tx('pp.67')} {g.next_destination || '—'}
                </div>
              )}
              <div className="pp-file-grid">
                {g.files.length === 0 && <span style={{ color: '#9ca3af', fontSize: '.85rem' }}>{tx('pp.68')}</span>}
                {g.files.map((f) => (
                  <div key={f.id} className="pp-file-card">
                    {isImageType(f.content_type) ? (
                      thumbs[f.id] ? (
                        <img
                          src={thumbs[f.id]}
                          alt={tx('pp.69', { p0: f.upload_seq })}
                          className="pp-thumb"
                          onClick={() => setLightbox(thumbs[f.id])}
                          title={tx('pp.70')}
                        />
                      ) : (
                        <div className="pp-thumb pp-thumb-loading">{tx('pp.34')}</div>
                      )
                    ) : (
                      <div
                        className="pp-thumb pp-thumb-pdf"
                        onClick={() => openFile(f.id, false)}
                        title={tx('pp.71')}
                      >
                        PDF
                      </div>
                    )}
                    <div className="pp-file-meta">#{f.upload_seq} · {(f.size_bytes / 1024 / 1024).toFixed(1)}MB</div>
                    <div className="pp-file-actions">
                      <button className="pp-btn-sm" onClick={() => openFile(f.id, true)}>DL</button>
                      <button className="pp-btn-ghost" style={{ padding: '.25rem .5rem' }} onClick={() => deleteFile(f.id)}>{tx('pp.72')}</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        </div>
      )}

      {/* 画像拡大表示（ライトボックス）。背景クリックで閉じる。 */}
      {lightbox && (
        <div className="pp-lightbox" onClick={() => setLightbox(null)}>
          <button className="pp-lightbox-close" onClick={() => setLightbox(null)} aria-label={tx('pp.27')}>×</button>
          <img src={lightbox} alt={tx('pp.73')} className="pp-lightbox-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
